// ════════════════════════════════════════════════════════════════════════
// DNACore V1 LEGACY ONLY — Do not add new features here.
// Tela legada do Boletim. Acesso oficial em /v2/academico/boletim.
// Rota V1 /boletim é redirecionada para /v2/academico/boletim em App.js.
// ════════════════════════════════════════════════════════════════════════
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Search, FileText, Download, Printer, GraduationCap, 
  TrendingUp, TrendingDown, AlertTriangle, CheckCircle, Clock,
  User, Calendar, BookOpen, BarChart3, MessageCircle, Save, PenLine
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, LineChart, Line } from 'recharts';


// ── Premium Boletim Document (tela + impressao). Somente apresentacao.
const SIT_ABBR = {
  'Aprovado': 'AP',
  'Aprovado em Recuperação': 'AR',
  'Em Recuperação Final': 'RF',
  'Conselho de Classe': 'CSC',
  'Reprovado por Falta': 'RPF',
  'Reprovado': 'RP',
  'Cursando': '—',
};

const sitTone = (s) => {
  switch (s) {
    case 'Aprovado': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    case 'Aprovado em Recuperação': return 'bg-sky-50 text-sky-700 border-sky-200';
    case 'Em Recuperação Final': return 'bg-amber-50 text-amber-700 border-amber-200';
    case 'Conselho de Classe': return 'bg-orange-50 text-orange-700 border-orange-200';
    case 'Reprovado por Falta':
    case 'Reprovado': return 'bg-red-50 text-red-700 border-red-200';
    default: return 'bg-slate-50 text-slate-600 border-slate-200';
  }
};

const notaTone = (v, min = 7) => {
  if (v === null || v === undefined || v === '' || v === '-') return 'text-slate-300';
  const n = Number(v);
  if (Number.isNaN(n)) return 'text-slate-600';
  if (n >= min) return 'text-emerald-700 font-semibold';
  if (n >= min - 1.5) return 'text-amber-600 font-semibold';
  return 'text-red-700 font-bold';
};

const fmt = (v) => (v === null || v === undefined || v === '' ? '—' : v);

const BoletimDoc = ({ boletim, via, viaSlug }) => {
  const cfg = boletim.configuracao || {};
  const tris = cfg.trimestres || [];
  const min = Number(cfg.media_minima ?? 7);
  const aluno = boletim.aluno || {};
  const resumo = boletim.resumo || {};
  const disciplinas = boletim.disciplinas || [];
  const emRec = disciplinas.filter((d) => d.recuperacao_final !== null && d.recuperacao_final !== undefined).length;
  const obs = (boletim.observacoes || resumo.observacoes || '').toString().trim();

  const th = 'px-1 py-1 text-center font-semibold border border-violet-200 whitespace-nowrap';
  const td = 'px-1 py-[3px] text-center border border-violet-100 tabular-nums';

  return (
    <section className="bol-via bg-white text-slate-900 rounded-xl border border-slate-200 p-4 sm:p-5 shadow-sm" data-testid={`boletim-via-${viaSlug}`}>
      {/* Cabecalho premium */}
      <header className="flex items-center gap-3 border-b-2 border-emerald-500 pb-2">
        <img src="/novo-dna-logo.png" alt="Novo DNA" className="h-11 w-auto object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        <div className="flex-1 min-w-0">
          <h2 className="text-base md:text-lg font-extrabold tracking-tight text-violet-950 leading-tight">
            BOLETIM ESCOLAR {aluno.ano_letivo}
          </h2>
          <p className="text-xs text-violet-700">NOVO DNA Instituto de Aprendizagem</p>
        </div>
        <span className="text-[10px] font-bold uppercase tracking-widest text-violet-700 border border-violet-200 bg-violet-50 rounded-full px-3 py-1">
          {via}
        </span>
      </header>

      {/* Identificacao */}
      <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-px bg-violet-100 border border-violet-200 rounded-lg overflow-hidden">
        {[
          ['Aluno(a)', aluno.nome],
          ['Matrícula', aluno.matricula],
          ['Nº chamada', fmt(aluno.numero_chamada)],
          ['Turma', aluno.turma],
          ['Situação', fmt(resumo.situacao_final)],
        ].map(([k, v]) => (
          <div key={k} className="bg-violet-50/70 px-2 py-1.5">
            <p className="text-[9px] uppercase tracking-wide text-violet-500">{k}</p>
            <p className="text-xs font-semibold text-violet-950 leading-snug break-words">{v || '—'}</p>
          </div>
        ))}
      </div>

      {/* Resumo academico */}
      <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2" data-testid={`boletim-resumo-${viaSlug}`}>
        {[
          ['Média Geral', resumo.media_geral ?? 'Em andamento'],
          ['Total de Faltas', resumo.total_faltas ?? 0],
          ['Situação', fmt(resumo.situacao_final)],
          ['Disciplinas em Recuperação', emRec],
        ].map(([k, v]) => (
          <div key={k} className="rounded-lg border border-violet-200 bg-gradient-to-b from-violet-50 to-white px-2 py-1.5 text-center">
            <p className="text-[9px] uppercase tracking-wide text-violet-500">{k}</p>
            <p className="text-sm font-bold text-violet-950">{v}</p>
          </div>
        ))}
      </div>

      {/* Tabela de notas */}
      <div className="mt-3 overflow-x-auto">
        <table className="w-full border-collapse text-[10px]">
          <thead>
            <tr className="bg-violet-900 text-white">
              <th rowSpan={2} className={`${th} text-left min-w-[110px]`}>Disciplina</th>
              {tris.map((tri) => (
                <th key={tri} colSpan={8} className={th}>{tri.toUpperCase()}</th>
              ))}
              <th colSpan={4} className={th}>RESULTADO FINAL</th>
            </tr>
            <tr className="bg-violet-700 text-white text-[9px]">
              {tris.map((tri) => (
                <React.Fragment key={`h-${tri}`}>
                  <th className={th}>APA</th>
                  <th className={th}>Pront.</th>
                  <th className={th}>Trab.</th>
                  <th className={th}>Av.T</th>
                  <th className={th}>Média</th>
                  <th className={th}>Rec.</th>
                  <th className={th}>Final</th>
                  <th className={th}>Faltas</th>
                </React.Fragment>
              ))}
              <th className={th}>Média</th>
              <th className={th}>Rec.F</th>
              <th className={th}>Nota</th>
              <th className={th}>Sit.</th>
            </tr>
          </thead>
          <tbody>
            {disciplinas.map((disc, i) => {
              const temRec = tris.some((t) => {
                const r = disc.trimestres?.[t]?.recuperacao;
                return r !== null && r !== undefined && r !== 'Pendente';
              }) || (disc.recuperacao_final !== null && disc.recuperacao_final !== undefined);
              return (
                <tr key={disc.id} className={temRec ? 'bg-yellow-50' : (i % 2 ? 'bg-violet-50/40' : 'bg-white')}>
                  <td className="px-1.5 py-[3px] border border-violet-100 font-semibold text-left text-[10px]">{disc.nome}</td>
                  {tris.map((tri) => {
                    const t = disc.trimestres?.[tri] || {};
                    const av = t.avaliacoes || {};
                    return (
                      <React.Fragment key={`${disc.id}-${tri}`}>
                        <td className={`${td} ${notaTone(av['APA'], min)}`}>{fmt(av['APA'])}</td>
                        <td className={`${td} ${notaTone(av['Prontidão'], min)}`}>{fmt(av['Prontidão'])}</td>
                        <td className={`${td} ${notaTone(av['Trabalho'], min)}`}>{fmt(av['Trabalho'])}</td>
                        <td className={`${td} ${notaTone(av['Avaliação Trimestral'], min)}`}>{fmt(av['Avaliação Trimestral'])}</td>
                        <td className={`${td} ${notaTone(t.media, min)}`}>{fmt(t.media)}</td>
                        <td className={`${td} ${notaTone(t.recuperacao === 'Pendente' ? null : t.recuperacao, min)}`}>
                          {t.recuperacao === 'Pendente' ? 'Pend' : fmt(t.recuperacao)}
                        </td>
                        <td className={`${td} font-bold ${notaTone(t.nota_final_trimestre, min)}`}>{fmt(t.nota_final_trimestre)}</td>
                        <td className={`${td} ${(disc.faltas?.[tri] || 0) > 0 ? 'text-red-600 font-semibold' : 'text-slate-300'}`}>
                          {disc.faltas?.[tri] || '—'}
                        </td>
                      </React.Fragment>
                    );
                  })}
                  <td className={`${td} ${notaTone(disc.media_final, min)}`}>{fmt(disc.media_final)}</td>
                  <td className={`${td} ${notaTone(disc.recuperacao_final, min)}`}>{fmt(disc.recuperacao_final)}</td>
                  <td className={`${td} font-bold ${notaTone(disc.nota_final, min)}`}>{fmt(disc.nota_final)}</td>
                  <td className={`${td} p-0`}>
                    <span className={`inline-block rounded border px-1 py-[1px] text-[9px] font-bold ${sitTone(disc.situacao)}`}>
                      {SIT_ABBR[disc.situacao] || 'CC'}
                    </span>
                  </td>
                </tr>
              );
            })}
            <tr className="bg-violet-100 font-bold">
              <td className="px-1.5 py-1 border border-violet-200 text-left text-[10px]">TOTAL DE FALTAS</td>
              {tris.map((tri) => (
                <React.Fragment key={`tf-${tri}`}>
                  <td colSpan={7} className="border border-violet-200"></td>
                  <td className={`${td} text-red-700`}>
                    {disciplinas.reduce((s, d) => s + (d.faltas?.[tri] || 0), 0)}
                  </td>
                </React.Fragment>
              ))}
              <td colSpan={3} className="border border-violet-200"></td>
              <td className={`${td} text-red-700`}>{resumo.total_faltas ?? 0}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Observacoes pedagogicas */}
      {obs && (
        <p className="mt-2 text-[10px] leading-snug text-slate-700 border border-slate-200 rounded-lg px-2 py-1.5 bg-slate-50">
          <strong className="text-violet-900">Observações Pedagógicas:</strong> {obs}
        </p>
      )}

      {/* Legenda */}
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[9px] text-slate-600 border border-slate-200 rounded-lg px-2 py-1.5">
        <strong className="text-violet-900 text-[9px]">Legenda:</strong>
        <span>AP — Aprovado</span>
        <span>AR — Aprovado em Recuperação</span>
        <span>RF — Em Recuperação Final</span>
        <span>CSC — Conselho de Classe</span>
        <span>RPF/RP — Reprovado</span>
        <span className="text-slate-400">|</span>
        <span>Média mínima: <strong>{cfg.media_minima}</strong></span>
        <span>Presença mínima: <strong>{cfg.presenca_minima}%</strong></span>
      </div>

      {/* Assinaturas */}
      <div className="mt-6 grid grid-cols-3 gap-6 text-center">
        {['Responsável / Família', 'Professor(a) / Coordenação', 'Direção'].map((s) => (
          <div key={s}>
            <div className="border-t border-slate-800 pt-1 text-[10px] text-slate-600">{s}</div>
          </div>
        ))}
      </div>
      <p className="mt-2 text-center text-[10px] text-slate-600">Data: ____ / ____ / ______</p>
    </section>
  );
};


// ═══════════════════════════════════════════════════════════════════
// VIEW OFICIAL DO BOLETIM (única fonte para tela oficial / impressão)
// Não exibe APA, Prontidão, Trabalho ou Av.T e nunca converte null em 0.
// ═══════════════════════════════════════════════════════════════════
const num = (v) => {
  if (v === null || v === undefined || v === '' || v === 'Pendente') return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
};

const barColor = (v, min) => (v >= min ? '#16A34A' : v >= min - 1.5 ? '#D97706' : '#DC2626');

const esc = (t) => String(t).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// SVG gerado como markup (evita wrappers HTML dentro do <svg> e garante impressão fiel)
const OfficialBarChart = ({ pairs, min }) => {
  if (!pairs.length) return null;
  const rowH = 17, labelW = 104, plotW = 300, w = labelW + plotW + 40;
  const h = pairs.length * rowH + 24;
  const grid = [0, 2, 4, 6, 8, 10].map((g) => {
    const x = labelW + (g / 10) * plotW;
    const isMin = g === min;
    return `<line x1="${x}" y1="4" x2="${x}" y2="${h - 18}" stroke="${isMin ? '#F59E0B' : '#E9E4F5'}" stroke-width="1"${isMin ? ' stroke-dasharray="3 3"' : ''} />`
      + `<text x="${x}" y="${h - 6}" font-size="7" fill="#6B7280" text-anchor="middle">${g}</text>`;
  }).join('');
  const bars = pairs.map(([nome, v], i) => {
    const y = 8 + i * rowH;
    return `<text x="${labelW - 6}" y="${y + 8}" font-size="8" fill="#374151" text-anchor="end">${esc(nome.slice(0, 18))}</text>`
      + `<rect x="${labelW}" y="${y}" width="${(v / 10) * plotW}" height="10" rx="2" fill="${barColor(v, min)}" />`
      + `<text x="${labelW + (v / 10) * plotW + 5}" y="${y + 8}" font-size="8" fill="#374151">${v.toFixed(1)}</text>`;
  }).join('');
  const svg = `<svg viewBox="0 0 ${w} ${h}" width="100%" xmlns="http://www.w3.org/2000/svg">${grid}${bars}</svg>`;
  return <div className="w-full" data-testid="oficial-grafico-barras" dangerouslySetInnerHTML={{ __html: svg }} />;
};

const OfficialLineChart = ({ points, labels, min }) => {
  const w = 380, h = 150, x0 = 26, y0 = h - 22, plotW = w - x0 - 14, plotH = y0 - 14;
  const step = plotW / (labels.length + 1);
  const coords = points
    .map((v, i) => (v === null ? null : { x: x0 + step * (i + 1), y: y0 - (v / 10) * plotH, v }))
    .filter(Boolean);
  const grid = [0, 2, 4, 6, 8, 10].map((g) => {
    const y = y0 - (g / 10) * plotH;
    return `<line x1="${x0}" y1="${y}" x2="${x0 + plotW}" y2="${y}" stroke="#E9E4F5" />`
      + `<text x="${x0 - 5}" y="${y + 3}" font-size="7" fill="#6B7280" text-anchor="end">${g}</text>`;
  }).join('');
  const ymin = y0 - (min / 10) * plotH;
  const refLine = `<line x1="${x0}" y1="${ymin}" x2="${x0 + plotW}" y2="${ymin}" stroke="#F59E0B" stroke-dasharray="3 3" />`
    + `<text x="${x0 + plotW}" y="${ymin - 4}" font-size="7" fill="#B45309" text-anchor="end">mínima ${min}</text>`;
  const axis = `<line x1="${x0}" y1="${y0}" x2="${x0 + plotW}" y2="${y0}" stroke="#D1D5DB" />`;
  const xlabels = labels.map((lb, i) =>
    `<text x="${x0 + step * (i + 1)}" y="${y0 + 12}" font-size="8" fill="#4B5563" text-anchor="middle">${esc(lb)}</text>`).join('');
  const poly = coords.length > 1
    ? `<polyline fill="none" stroke="#64327D" stroke-width="2" points="${coords.map((c) => `${c.x},${c.y}`).join(' ')}" />`
    : '';
  const dots = coords.map((c) =>
    `<circle cx="${c.x}" cy="${c.y}" r="3.5" fill="#64327D" stroke="#fff" stroke-width="1" />`
    + `<text x="${c.x}" y="${c.y - 7}" font-size="8" font-weight="bold" fill="#3B0764" text-anchor="middle">${c.v.toFixed(1)}</text>`).join('');
  const vazio = coords.length === 0
    ? `<text x="${x0 + plotW / 2}" y="${y0 - plotH / 2}" font-size="9" fill="#9CA3AF" text-anchor="middle">Aguardando fechamento do 1º trimestre</text>`
    : '';
  const svg = `<svg viewBox="0 0 ${w} ${h}" width="100%" xmlns="http://www.w3.org/2000/svg">${grid}${refLine}${axis}${xlabels}${poly}${dots}${vazio}</svg>`;
  return <div className="w-full" data-testid="oficial-grafico-evolucao" dangerouslySetInnerHTML={{ __html: svg }} />;
};

const OfficialReportCard = ({ boletim }) => {
  const cfg = boletim.configuracao || {};
  const tris = cfg.trimestres || [];
  const min = Number(cfg.media_minima ?? 6);
  const aluno = boletim.aluno || {};
  const resumo = boletim.resumo || {};
  const discs = boletim.disciplinas || [];
  const emRec = discs.filter((d) => d.recuperacao_final !== null && d.recuperacao_final !== undefined).length;
  const obs = (boletim.observacoes || '').toString().trim();
  const qrSrc = boletim.share_token
    ? `${process.env.REACT_APP_BACKEND_URL}/api/boletim/qr/${boletim.share_token}.png`
    : null;

  // gráfico 1 — média por disciplina (somente valores reais)
  const perf = [];
  discs.forEach((d) => {
    let v = num(d.media_final);
    if (v === null) {
      for (let i = tris.length - 1; i >= 0; i--) {
        const t = d.trimestres?.[tris[i]] || {};
        const cand = num(t.nota_final_trimestre) ?? num(t.media);
        if (cand !== null) { v = cand; break; }
      }
    }
    if (v !== null) perf.push([d.nome, v]);
  });

  // gráfico 2 — evolução da média geral (null = trimestre sem fechamento)
  const evo = tris.map((tri) => {
    const vals = discs
      .map((d) => num(d.trimestres?.[tri]?.nota_final_trimestre) ?? num(d.trimestres?.[tri]?.media))
      .filter((v) => v !== null);
    return vals.length ? Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10 : null;
  });
  const temGrafico = perf.length > 0 || evo.some((v) => v !== null);

  const th = 'px-1 py-1.5 text-center font-semibold text-[10px] whitespace-nowrap';
  const td = 'px-1 py-1.5 text-center text-[11px] tabular-nums';

  return (
    <div className="bol-official bg-white text-slate-900 p-5 sm:p-7" data-testid="boletim-oficial">
      {/* Cabeçalho */}
      <header className="flex items-center gap-4 border-b-2 border-emerald-500 pb-3">
        <img src="/novo-dna-logo.png" alt="Novo DNA" className="h-14 w-auto object-contain"
             onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        <div className="flex-1 min-w-0">
          <h2 className="text-xl md:text-2xl font-extrabold tracking-tight text-violet-950 leading-none">
            BOLETIM ESCOLAR {aluno.ano_letivo}
          </h2>
          <p className="text-xs text-violet-700 mt-1">NOVO DNA Instituto de Aprendizagem</p>
          <p className="text-[10px] text-slate-400">Via da Família · documento oficial</p>
        </div>
        {qrSrc && <img src={qrSrc} alt="QR de autenticidade" className="h-16 w-16" data-testid="boletim-oficial-qr" />}
      </header>

      {/* Identificação */}
      <div className="mt-4 grid grid-cols-2 md:grid-cols-6 gap-px bg-violet-100 border border-violet-200 rounded-lg overflow-hidden">
        {[
          ['Aluno(a)', aluno.nome],
          ['Matrícula', aluno.matricula],
          ['Nº chamada', fmt(aluno.numero_chamada)],
          ['Turma', aluno.turma],
          ['Ano letivo', aluno.ano_letivo],
          ['Situação', resumo.situacao_final || 'Cursando'],
        ].map(([k, v]) => (
          <div key={k} className="bg-white px-2.5 py-2">
            <p className="text-[9px] uppercase tracking-wide text-violet-500">{k}</p>
            <p className="text-xs font-semibold text-slate-900 leading-snug break-words">{v || '—'}</p>
          </div>
        ))}
      </div>

      {/* Tabela oficial */}
      <div className="mt-4 overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-violet-950 text-white">
              <th rowSpan={2} className={`${th} text-left min-w-[130px]`}>Disciplina</th>
              {tris.map((tri) => <th key={tri} colSpan={4} className={th}>{tri.toUpperCase()}</th>)}
              <th colSpan={4} className={th}>RESULTADO FINAL</th>
            </tr>
            <tr className="bg-violet-700 text-white">
              {tris.map((tri) => (
                <React.Fragment key={`h-${tri}`}>
                  <th className={th}>Média</th>
                  <th className={th}>Rec.</th>
                  <th className={th}>Média Final</th>
                  <th className={th}>Faltas</th>
                </React.Fragment>
              ))}
              <th className={th}>Média Final</th>
              <th className={th}>Rec. Final</th>
              <th className={th}>Nota Final</th>
              <th className={th}>Situação</th>
            </tr>
          </thead>
          <tbody>
            {discs.map((d) => {
              const temRec = tris.some((t) => num(d.trimestres?.[t]?.recuperacao) !== null)
                || num(d.recuperacao_final) !== null;
              return (
                <tr key={d.id} className={`border-b border-slate-100 ${temRec ? 'bg-yellow-50' : ''}`}>
                  <td className="px-2 py-1.5 text-left text-[11px] font-semibold border-r border-violet-100">{d.nome}</td>
                  {tris.map((tri) => {
                    const t = d.trimestres?.[tri] || {};
                    const rec = num(t.recuperacao);
                    return (
                      <React.Fragment key={`${d.id}-${tri}`}>
                        <td className={`${td} font-semibold ${notaTone(num(t.media), min)}`}>{fmt(num(t.media))}</td>
                        <td className={`${td} ${notaTone(rec, min)}`}>{fmt(rec)}</td>
                        <td className={`${td} font-semibold ${notaTone(num(t.nota_final_trimestre), min)}`}>
                          {fmt(num(t.nota_final_trimestre))}
                        </td>
                        <td className={`${td} border-r border-violet-100 ${d.faltas?.[tri] ? 'text-slate-700' : 'text-slate-300'}`}>
                          {d.faltas?.[tri] || '—'}
                        </td>
                      </React.Fragment>
                    );
                  })}
                  <td className={`${td} font-semibold ${notaTone(num(d.media_final), min)}`}>{fmt(num(d.media_final))}</td>
                  <td className={`${td} ${notaTone(num(d.recuperacao_final), min)}`}>{fmt(num(d.recuperacao_final))}</td>
                  <td className={`${td} font-bold ${notaTone(num(d.nota_final), min)}`}>{fmt(num(d.nota_final))}</td>
                  <td className={`${td} text-slate-700`}>{d.situacao || '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Gráficos */}
      {temGrafico && (
        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-5 bol-charts">
          {perf.length > 0 && (
            <div>
              <p className="text-[11px] font-bold text-violet-950 mb-1">Desempenho por disciplina (média)</p>
              <OfficialBarChart pairs={perf} min={min} />
            </div>
          )}
          <div>
            <p className="text-[11px] font-bold text-violet-950 mb-1">Evolução da média geral</p>
            <OfficialLineChart points={evo} labels={tris.map((t) => t.replace('º Trimestre', 'º Tri'))} min={min} />
          </div>
        </div>
      )}

      {/* Resumo do ano */}
      <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-px bg-violet-100 border border-violet-200 rounded-lg overflow-hidden">
        {[
          ['Média Geral', resumo.media_geral ?? '—'],
          ['Total de Faltas', resumo.total_faltas ?? 0],
          ['Disciplinas em Recuperação', emRec],
          ['Situação', resumo.situacao_final || 'Cursando'],
        ].map(([k, v]) => (
          <div key={k} className="bg-violet-50/50 px-3 py-2">
            <p className="text-[9px] uppercase tracking-wide text-violet-500">{k}</p>
            <p className="text-base font-bold text-violet-950">{v}</p>
          </div>
        ))}
      </div>

      {obs && (
        <p className="mt-3 text-[11px] leading-snug text-slate-700">
          <strong className="text-violet-900">Observações Pedagógicas:</strong> {obs}
        </p>
      )}

      {/* Assinaturas */}
      <div className="mt-10 grid grid-cols-3 gap-8 text-center bol-sign">
        {['Responsável / Família', 'Professor(a) / Coordenação', 'Direção'].map((sg) => (
          <div key={sg} className="border-t border-slate-800 pt-1 text-[11px] text-slate-600">{sg}</div>
        ))}
      </div>
      <p className="mt-3 text-center text-[11px] text-slate-700">Data: ____ / ____ / ______</p>
      <p className="mt-2 text-center text-[9px] text-slate-500">
        Autenticidade: leia o QR Code para validar este boletim. &nbsp;|&nbsp; Média mínima para aprovação: {cfg.media_minima}
        &nbsp;·&nbsp; Presença mínima: {cfg.presenca_minima}% &nbsp;·&nbsp; “—” indica lançamento ainda não fechado (não equivale a zero).
      </p>
    </div>
  );
};

export default function BoletimPage() {
  const [searchParams] = useSearchParams();
  const [students, setStudents] = useState([]);
  const [classGroups, setClassGroups] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedClassGroup, setSelectedClassGroup] = useState('');
  const [boletim, setBoletim] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [year, setYear] = useState(Number(searchParams.get('year')) || 2026);
  const [obsText, setObsText] = useState('');
  const [savingObs, setSavingObs] = useState(false);
  const [sendingWa, setSendingWa] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  // Carregar boletim se student_id vier na URL
  useEffect(() => {
    const studentIdFromUrl = searchParams.get('student_id');
    if (studentIdFromUrl && students.length > 0) {
      const student = students.find(s => s.id === studentIdFromUrl);
      if (student) {
        setSelectedStudent(student);
        loadBoletim(studentIdFromUrl);
      }
    }
  }, [searchParams, students]);

  // Recarrega o boletim quando o ano letivo muda (V7.3)
  useEffect(() => {
    if (selectedStudent?.id) loadBoletim(selectedStudent.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  const loadData = async () => {
    try {
      const [studentsRes, classGroupsRes] = await Promise.all([
        api.get('/students'),
        api.get('/class-groups')
      ]);
      setStudents(studentsRes.data);
      setClassGroups(classGroupsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    }
  };

  const loadBoletim = async (studentId) => {
    if (!studentId) return;
    
    setLoading(true);
    try {
      const response = await api.get(`/boletim/${studentId}?year=${year}`);
      setBoletim(response.data);
      setObsText(response.data?.observacoes || '');
    } catch (error) {
      toast.error('Erro ao carregar boletim');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectStudent = (student) => {
    setSelectedStudent(student);
    loadBoletim(student.id);
  };

  const handleSaveObs = async () => {
    if (!selectedStudent) return;
    setSavingObs(true);
    try {
      await api.put(`/boletim/${selectedStudent.id}/observacoes?year=${year}`, { observacoes: obsText });
      setBoletim((b) => (b ? { ...b, observacoes: obsText.trim() } : b));
      toast.success('Observações salvas — já saem no boletim impresso e no PDF');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao salvar observações');
    } finally {
      setSavingObs(false);
    }
  };

  const handleSendWhatsApp = async () => {
    if (!selectedStudent) return;
    setSendingWa(true);
    try {
      const { data } = await api.post(`/boletim/${selectedStudent.id}/whatsapp?year=${year}`);
      window.open(data.whatsapp_link, '_blank');
      toast.success(`WhatsApp aberto para ${data.responsible_name || 'o responsável'}`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erro ao gerar link do WhatsApp');
    } finally {
      setSendingWa(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!selectedStudent) return;
    
    try {
      const response = await api.get(`/boletim/${selectedStudent.id}/pdf?year=${year}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `boletim_${selectedStudent.registration_number}_${year}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('PDF gerado com sucesso!');
    } catch (error) {
      toast.error('Erro ao gerar PDF');
    }
  };

  const handleDownloadTurmaPDF = async () => {
    if (!selectedClassGroup || selectedClassGroup === 'all') {
      toast.error('Selecione uma turma específica');
      return;
    }
    
    // Encontrar o ID da turma
    const turma = classGroups.find(cg => cg.name === selectedClassGroup);
    if (!turma) {
      toast.error('Turma não encontrada');
      return;
    }
    
    toast.info('Gerando boletins da turma... Isso pode levar alguns segundos.');
    
    try {
      const response = await api.get(`/boletim/turma/${turma.id}/pdf?year=${year}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `boletins_${selectedClassGroup}_${year}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('PDF da turma gerado com sucesso!');
    } catch (error) {
      toast.error('Erro ao gerar PDF da turma');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const filteredStudents = students.filter(s => {
    const matchesSearch = s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          s.registration_number?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesClass = !selectedClassGroup || selectedClassGroup === 'all' || s.class_name === selectedClassGroup;
    return matchesSearch && matchesClass;
  });

  const getSituacaoColor = (situacao) => {
    switch(situacao) {
      case 'Aprovado': return 'bg-green-100 text-green-800 border-green-200';
      case 'Aprovado em Recuperação': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Em Recuperação Final': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Conselho de Classe': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'Reprovado por Falta': return 'bg-red-100 text-red-800 border-red-200';
      case 'Reprovado': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getNotaColor = (nota, media = 7) => {
    if (nota === null || nota === undefined || nota === '-') return '';
    if (nota >= media) return 'text-green-600 font-semibold';
    if (nota >= media - 1) return 'text-yellow-600';
    return 'text-red-600 font-semibold';
  };

  const COLORS = ['#4CAF50', '#2196F3', '#FF9800', '#f44336', '#9C27B0', '#00BCD4', '#795548', '#607D8B'];

  // Dados para gráficos
  const getChartData = () => {
    if (!boletim) return { radar: [], bar: [], pie: [], line: [] };
    
    const disciplinas = boletim.disciplinas || [];
    
    // Dados para radar
    const radar = disciplinas.map(d => ({
      subject: d.nome.substring(0, 10),
      media: d.media_final ?? d.nota_final ?? null,
      fullMark: 10
    }));
    
    // Dados para gráfico de barras (notas por trimestre)
    const bar = disciplinas.slice(0, 6).map(d => ({
      name: d.nome.substring(0, 8),
      '1T': d.trimestres['1º Trimestre']?.nota_final_trimestre ?? null,
      '2T': d.trimestres['2º Trimestre']?.nota_final_trimestre ?? null,
      '3T': d.trimestres['3º Trimestre']?.nota_final_trimestre ?? null,
    }));
    
    // Dados para pizza (situação)
    const situacaoCount = {};
    disciplinas.forEach(d => {
      situacaoCount[d.situacao] = (situacaoCount[d.situacao] || 0) + 1;
    });
    const pie = Object.entries(situacaoCount).map(([name, value]) => ({ name, value }));
    
    // Dados para linha (evolução)
    const line = ['1º Trimestre', '2º Trimestre', '3º Trimestre'].map(tri => {
      const notas = disciplinas.map(d => d.trimestres[tri]?.nota_final_trimestre).filter(n => n !== null && n !== undefined);
      return {
        name: tri.replace('º Trimestre', 'T'),
        media: notas.length > 0 ? Number((notas.reduce((a, b) => a + b, 0) / notas.length).toFixed(1)) : null
      };
    });
    
    return { radar, bar, pie, line };
  };

  const chartData = getChartData();

  return (
    <div className="p-3 sm:p-6 space-y-4 sm:space-y-6" data-testid="boletim-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 print:hidden">
        <div>
          <h1 className="text-xl sm:text-3xl font-bold">Boletim Escolar</h1>
          <p className="text-sm text-muted-foreground">Visualize e imprima boletins dos alunos</p>
        </div>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          {selectedClassGroup && selectedClassGroup !== 'all' && (
            <Button variant="outline" onClick={handleDownloadTurmaPDF} className="flex-1 sm:flex-none text-xs sm:text-sm">
              <Download className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Baixar Todos da Turma</span>
              <span className="sm:hidden">Turma</span>
            </Button>
          )}
          {boletim && (
            <>
              <Button
                variant="outline"
                onClick={handleSendWhatsApp}
                disabled={sendingWa}
                className="flex-1 sm:flex-none text-xs sm:text-sm border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                data-testid="boletim-whatsapp-btn"
              >
                <MessageCircle className="w-4 h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">{sendingWa ? 'Gerando...' : 'Enviar à Família'}</span>
                <span className="sm:hidden">WhatsApp</span>
              </Button>
              <Button variant="outline" onClick={handleDownloadPDF} className="flex-1 sm:flex-none text-xs sm:text-sm" data-testid="boletim-pdf-btn">
                <Download className="w-4 h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Baixar PDF</span>
                <span className="sm:hidden">PDF</span>
              </Button>
              <Button onClick={handlePrint} className="flex-1 sm:flex-none text-xs sm:text-sm" data-testid="boletim-print-btn">
                <Printer className="w-4 h-4 mr-1 sm:mr-2" />
                Imprimir
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-6">
        {/* Painel de Seleção */}
        <Card className="lg:col-span-1 print:hidden">
          <CardHeader className="p-3 sm:p-6">
            <CardTitle className="text-base sm:text-lg flex items-center gap-2">
              <User className="w-4 h-4 sm:w-5 sm:h-5" />
              Selecionar Aluno
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 sm:space-y-4 p-3 sm:p-6 pt-0 sm:pt-0">
            <div className="space-y-2">
              <Label className="text-sm">Turma</Label>
              <Select value={selectedClassGroup || "all"} onValueChange={setSelectedClassGroup}>
                <SelectTrigger className="text-sm">
                  <SelectValue placeholder="Todas as turmas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as turmas</SelectItem>
                  {classGroups.map(cg => (
                    <SelectItem key={cg.id} value={cg.name}>{cg.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label className="text-sm">Ano Letivo</Label>
              <select
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                data-testid="boletim-year-select"
              >
                {[2026, 2027, 2028].map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>

            <div className="space-y-2">
              <Label className="text-sm">Buscar Aluno</Label>
              <div className="relative">
                <Search className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Nome ou matrícula..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 text-sm"
                />
              </div>
            </div>

            <div className="border rounded-lg max-h-[300px] sm:max-h-[400px] overflow-y-auto">
              {filteredStudents.slice(0, 50).map(student => (
                <div
                  key={student.id}
                  className={`p-2 sm:p-3 border-b cursor-pointer hover:bg-muted/50 transition-colors active:bg-muted ${selectedStudent?.id === student.id ? 'bg-primary/10 border-l-4 border-l-primary' : ''}`}
                  onClick={() => handleSelectStudent(student)}
                >
                  <p className="font-medium text-xs sm:text-sm truncate">{student.name}</p>
                  <p className="text-[10px] sm:text-xs text-muted-foreground">{student.registration_number} • {student.class_name?.split(' - ')[0]}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Área do Boletim */}
        <div className="lg:col-span-3 space-y-4 sm:space-y-6">
          {loading ? (
            <Card>
              <CardContent className="p-8 sm:p-12 text-center">
                <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-primary mx-auto"></div>
                <p className="mt-4 text-sm text-muted-foreground">Carregando boletim...</p>
              </CardContent>
            </Card>
          ) : !boletim ? (
            <Card>
              <CardContent className="p-8 sm:p-12 text-center">
                <GraduationCap className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-muted-foreground/50" />
                <h3 className="mt-4 text-base sm:text-lg font-medium">Selecione um aluno</h3>
                <p className="text-sm text-muted-foreground">Escolha um aluno na lista para visualizar o boletim</p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Abas: Notas e Gráficos */}
              <Tabs defaultValue="notas" className="space-y-4">
                <TabsList className="print:hidden">
                  <TabsTrigger value="notas">
                    <BookOpen className="w-4 h-4 mr-2" />
                    Notas Detalhadas
                  </TabsTrigger>
                  <TabsTrigger value="graficos">
                    <BarChart3 className="w-4 h-4 mr-2" />
                    Gráficos
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="notas" className="print:block">
                  <Card className="print:hidden mb-4 border-violet-200" data-testid="boletim-obs-editor">
                    <CardContent className="p-3 sm:p-4 space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <Label className="text-sm flex items-center gap-2 text-violet-900">
                          <PenLine className="w-4 h-4" />
                          Observações Pedagógicas da Coordenação
                        </Label>
                        <Button
                          size="sm"
                          onClick={handleSaveObs}
                          disabled={savingObs}
                          className="text-xs"
                          data-testid="boletim-obs-save"
                        >
                          <Save className="w-3.5 h-3.5 mr-1.5" />
                          {savingObs ? 'Salvando...' : 'Salvar'}
                        </Button>
                      </div>
                      <textarea
                        value={obsText}
                        onChange={(e) => setObsText(e.target.value)}
                        maxLength={1500}
                        rows={3}
                        placeholder="Escreva aqui a observação que deve sair no boletim impresso e no PDF (ex.: avanços, pontos de atenção, encaminhamentos)..."
                        className="w-full rounded-lg border border-violet-200 bg-white p-2.5 text-sm outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100 resize-y"
                        data-testid="boletim-obs-input"
                      />
                      <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                        <span>
                          {boletim.observacoes_meta?.updated_by_name
                            ? `Última edição por ${boletim.observacoes_meta.updated_by_name}`
                            : 'Aparece no boletim oficial (tela, impressão e PDF) quando preenchida.'}
                        </span>
                        <span>{obsText.length}/1500</span>
                      </div>
                    </CardContent>
                  </Card>
                  <div className="print:hidden" data-testid="boletim-doc-area">
                    <BoletimDoc boletim={boletim} via="Acompanhamento interno" viaSlug="interno" />
                  </div>

                </TabsContent>

                <TabsContent value="graficos" className="print:hidden">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Gráfico Radar */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Desempenho por Disciplina</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="h-[300px]">
                          <ResponsiveContainer width="100%" height="100%">
                            <RadarChart data={chartData.radar}>
                              <PolarGrid />
                              <PolarAngleAxis dataKey="subject" tick={{ fontSize: 10 }} />
                              <PolarRadiusAxis angle={30} domain={[0, 10]} />
                              <Radar name="Média" dataKey="media" stroke="#4F46E5" fill="#4F46E5" fillOpacity={0.5} />
                              <Tooltip />
                            </RadarChart>
                          </ResponsiveContainer>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Gráfico de Barras - Notas por Trimestre */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Notas por Trimestre</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="h-[300px]">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={chartData.bar}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                              <YAxis domain={[0, 10]} />
                              <Tooltip />
                              <Legend />
                              <Bar dataKey="1T" name="1º Trim" fill="#4CAF50" />
                              <Bar dataKey="2T" name="2º Trim" fill="#2196F3" />
                              <Bar dataKey="3T" name="3º Trim" fill="#FF9800" />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Gráfico de Pizza - Situação */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Situação das Disciplinas</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="h-[300px]">
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={chartData.pie}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                label={({ name, percent }) => `${name.substring(0, 10)} (${(percent * 100).toFixed(0)}%)`}
                                outerRadius={100}
                                fill="#8884d8"
                                dataKey="value"
                              >
                                {chartData.pie.map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                              </Pie>
                              <Tooltip />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Gráfico de Linha - Evolução */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Evolução da Média Geral</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="h-[300px]">
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={chartData.line}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="name" />
                              <YAxis domain={[0, 10]} />
                              <Tooltip />
                              <Legend />
                              <Line type="monotone" dataKey="media" name="Média" stroke="#4F46E5" strokeWidth={3} dot={{ r: 6 }} connectNulls />
                              {/* Linha de referência da média mínima */}
                              <Line type="monotone" dataKey={() => boletim.configuracao.media_minima} name="Média Mínima" stroke="#f44336" strokeDasharray="5 5" dot={false} />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>
              </Tabs>

              {/* ── DOCUMENTO OFICIAL (mesma view usada em Imprimir / PDF / envio à família) */}
              <Card className="print:shadow-none print:border-0 border-violet-200" data-testid="boletim-oficial-card">
                <CardHeader className="print:hidden pb-2">
                  <CardTitle className="text-base md:text-lg flex items-center gap-2">
                    <GraduationCap className="w-4 h-4 text-violet-700" />
                    Boletim Oficial · versão da família
                  </CardTitle>
                  <p className="text-xs text-muted-foreground">
                    Esta é exatamente a versão usada em Imprimir, Baixar PDF, envio à família e DNA Connect.
                  </p>
                </CardHeader>
                <CardContent className="p-0">
                  <div id="boletim-print-area">
                    <OfficialReportCard boletim={boletim} />
                  </div>
                </CardContent>
              </Card>


            </>
          )}
        </div>
      </div>

      {/* Estilos de impressão — documento oficial */}
      <style>{`
        @media print {
          html, body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            background: #fff !important;
          }
          body * { visibility: hidden !important; }
          #boletim-print-area, #boletim-print-area * { visibility: visible !important; }
          #boletim-print-area {
            position: absolute !important;
            left: 0; top: 0; width: 100%;
            margin: 0 !important; padding: 0 !important;
          }
          #boletim-print-area .bol-official {
            border: none !important; box-shadow: none !important;
            border-radius: 0 !important; padding: 0 !important;
          }
          #boletim-print-area table { width: 100% !important; table-layout: fixed; font-size: 8pt !important; }
          #boletim-print-area thead { display: table-header-group; }
          #boletim-print-area tr, #boletim-print-area td, #boletim-print-area th { break-inside: avoid; page-break-inside: avoid; }
          #boletim-print-area .bol-charts, #boletim-print-area .bol-sign { break-inside: avoid; page-break-inside: avoid; }
          #boletim-print-area .bol-charts svg { width: 100% !important; height: 44mm !important; }
          #boletim-print-area .overflow-x-auto { overflow: visible !important; }
          @page { size: A4 landscape; margin: 8mm; }
        }
      `}</style>


    </div>
  );
}
